import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { TFetchStatus } from "./types";

const BASE_URL = import.meta.env.REACT_APP_API_ENDPOINT + "/advance/custom-statuses";

const rejectFromAxios = (error: unknown) => (axios.isAxiosError(error) ? error.response?.data : undefined);

const toastFromPayload = (payload: unknown) => {
    const message =
        payload && typeof payload === "object" && "message" in payload
            ? String((payload as { message?: string }).message)
            : undefined;
    toast.error(translationsWithFallback(message ?? "", "Settings.AdvanceStatusError"));
};

export type IAdvanceCustomStatus = {
    id: number;
    name: string;
    customIdentifier: string | null;
    companyId: number;
};

export const fetchAdvanceCustomStatuses = createAsyncThunk(
    "advanceCustomStatuses/fetchAll",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(BASE_URL);
            return response.data.data as IAdvanceCustomStatus[];
        } catch (error: unknown) {
            return rejectWithValue(rejectFromAxios(error));
        }
    },
);

export const createAdvanceCustomStatus = createAsyncThunk(
    "advanceCustomStatuses/create",
    async ({ name, customIdentifier }: { name: string; customIdentifier: string | null }, { rejectWithValue }) => {
        try {
            const response = await axios.post(BASE_URL, { name, customIdentifier });
            return response.data.data as IAdvanceCustomStatus;
        } catch (error: unknown) {
            return rejectWithValue(rejectFromAxios(error));
        }
    },
);

export const updateAdvanceCustomStatus = createAsyncThunk(
    "advanceCustomStatuses/update",
    async (
        { id, name, customIdentifier }: { id: number; name: string; customIdentifier: string | null },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(`${BASE_URL}/${id}`, { name, customIdentifier });
            return response.data.data as IAdvanceCustomStatus;
        } catch (error: unknown) {
            return rejectWithValue(rejectFromAxios(error));
        }
    },
);

export const deleteAdvanceCustomStatus = createAsyncThunk(
    "advanceCustomStatuses/delete",
    async ({ id }: { id: number }, { rejectWithValue }) => {
        try {
            await axios.delete(`${BASE_URL}/${id}`);
            return id;
        } catch (error: unknown) {
            return rejectWithValue(rejectFromAxios(error));
        }
    },
);

type AdvanceCustomStatusState = {
    items: IAdvanceCustomStatus[];
    status: TFetchStatus;
};

const initialState: AdvanceCustomStatusState = {
    items: [],
    status: "idle",
};

const advanceCustomStatusSlice = createSlice({
    name: "advanceCustomStatuses",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchAdvanceCustomStatuses.pending, (state) => {
            state.status = "pending";
        });
        builder.addCase(fetchAdvanceCustomStatuses.fulfilled, (state, action) => {
            state.status = "succeeded";
            state.items = action.payload;
        });
        builder.addCase(fetchAdvanceCustomStatuses.rejected, (state) => {
            state.status = "failed";
        });
        builder.addCase(createAdvanceCustomStatus.fulfilled, (state, action) => {
            state.items = [...state.items, action.payload].sort((a, b) => a.name.localeCompare(b.name));
            toast.success(`${t("Settings.AdvanceStatusCreated")}`);
        });
        builder.addCase(createAdvanceCustomStatus.rejected, (_state, { payload }) => {
            toastFromPayload(payload);
        });
        builder.addCase(updateAdvanceCustomStatus.fulfilled, (state, action) => {
            state.items = state.items.map((item) => (item.id === action.payload.id ? action.payload : item));
            toast.success(`${t("Settings.AdvanceStatusUpdated")}`);
        });
        builder.addCase(updateAdvanceCustomStatus.rejected, (_state, { payload }) => {
            toastFromPayload(payload);
        });
        builder.addCase(deleteAdvanceCustomStatus.fulfilled, (state, action) => {
            state.items = state.items.filter((item) => item.id !== action.payload);
            toast.success(`${t("Settings.AdvanceStatusDeleted")}`);
        });
        builder.addCase(deleteAdvanceCustomStatus.rejected, (_state, { payload }) => {
            toastFromPayload(payload);
        });
    },
});

export default advanceCustomStatusSlice.reducer;
